import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  calcDebt,
  calcEquityStructure,
  calcIncreasedValue,
  calcSimpleUnderwriting,
} from '@bcc/shared';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD } from '../../constants/siteUi';

const TABS = [
  { id: 'increased', label: 'Increased value' },
  { id: 'underwriting', label: 'Underwriting' },
  { id: 'debt', label: 'Debt' },
  { id: 'equity', label: 'Equity' },
] as const;

type TabId = (typeof TABS)[number]['id'];

type Props = {
  open: boolean;
  onClose: () => void;
  /** Merge into parent deal `underwriting` JSON */
  initialMerged?: Record<string, unknown>;
  onApply: (underwriting: Record<string, unknown>) => void;
};

function fmt$(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
    n
  );
}

export function CrmToolsDialog({ open, onClose, initialMerged, onApply }: Props) {
  const [tab, setTab] = useState<TabId>('increased');
  const reduceMotion = useReducedMotion();
  const dialogTransition = reduceMotion ? { duration: 0 } : { duration: 0.22, ease: [0.22, 1, 0.36, 1] as const };

  const [iv, setIv] = useState({ units: 100, rentIncreaseMo: 200, occupancyPct: 90, capRatePct: 5 });
  const [uw, setUw] = useState({
    units: 100,
    rentPerUnitMo: 1500,
    otherIncomePct: 2,
    occupancyPct: 90,
    expensesPct: 40,
    capRatePct: 5,
  });
  const [debt, setDebt] = useState({
    purchasePrice: 2_000_000,
    units: 100,
    grossRentalIncomeYr: 1_800_000,
    vacancyPct: 5,
    otherIncomeYr: 30_000,
    operatingExpensesYr: 500_000,
    ltvPct: 75,
    interestRatePct: 6.5,
    amortYears: 30,
  });
  const [eq, setEq] = useState({
    totalProjectCost: 5_000_000,
    loanAmount: 3_500_000,
    holdingYears: 5,
    annualNoi: 400_000,
    exitCapRatePct: 5.5,
    annualLoanPayment: 210_000,
    gpEquityPct: 10,
    preferredReturnLpPct: 8,
    gpPromotePct: 20,
  });

  useEffect(() => {
    if (!open || !initialMerged) return;
    const m = initialMerged;
    if (m.increasedValue && typeof m.increasedValue === 'object')
      setIv({ ...iv, ...(m.increasedValue as typeof iv) });
    if (m.simpleUnderwriting && typeof m.simpleUnderwriting === 'object')
      setUw({ ...uw, ...(m.simpleUnderwriting as typeof uw) });
    if (m.debt && typeof m.debt === 'object') setDebt({ ...debt, ...(m.debt as typeof debt) });
    if (m.equity && typeof m.equity === 'object') setEq({ ...eq, ...(m.equity as typeof eq) });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once when opening
  }, [open, initialMerged]);

  const ivRes = useMemo(() => calcIncreasedValue(iv), [iv]);
  const uwRes = useMemo(() => calcSimpleUnderwriting(uw), [uw]);
  const debtRes = useMemo(() => calcDebt(debt), [debt]);
  const eqRes = useMemo(() => calcEquityStructure(eq), [eq]);

  function saveToParent() {
    onApply({
      increasedValue: { ...iv },
      simpleUnderwriting: { ...uw },
      debt: { ...debt },
      equity: { ...eq },
      computed: {
        increasedValue: ivRes,
        simpleUnderwriting: uwRes,
        debt: debtRes,
        equity: eqRes,
      },
    });
    onClose();
  }

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70"
          role="dialog"
          aria-modal="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={dialogTransition}
          onClick={onClose}
        >
          <motion.div
            className={`flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden shadow-xl ${SITE_GLASS_CARD}`}
            initial={reduceMotion ? undefined : { opacity: 0, scale: 0.97, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, scale: 0.97, y: 14 }}
            transition={dialogTransition}
            onClick={(e) => e.stopPropagation()}
          >
        <header className="flex shrink-0 items-center justify-between gap-4 border-b border-white/[0.08] px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-[var(--home-text-on-dark)]">Underwriting tools</h2>
            <p className="mt-0.5 text-xs text-[var(--home-muted-on-dark)]">
              Flip between calculators and apply to the open deal.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={saveToParent}
              className="rounded-full bg-[var(--home-miami-blue)] px-4 py-2 text-sm font-medium text-white hover:brightness-110"
            >
              Apply to deal
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-3 text-sm text-[var(--home-muted-on-dark)] hover:text-[var(--home-text-on-dark)]"
            >
              Close
            </button>
          </div>
        </header>
        <div className="flex shrink-0 overflow-x-auto border-b border-white/[0.08]">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px ${
                tab === t.id
                  ? 'border-[var(--home-miami-blue)] text-[var(--home-text-on-dark)]'
                  : 'border-transparent text-[var(--home-muted-on-dark)] hover:text-[var(--home-text-on-dark)]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="overflow-y-auto p-5 flex-1 text-sm">
          {tab === 'increased' && (
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="font-medium text-[var(--home-text-on-dark)]">Deal inputs</h3>
                {(
                  [
                    ['units', 'Number of units', 0, 1200, 1],
                    ['rentIncreaseMo', 'Rent increase ($/mo)', 0, 2000, 1],
                    ['occupancyPct', 'Occupancy (%)', 0, 100, 1],
                    ['capRatePct', 'Cap rate (%)', 0.01, 20, 0.01],
                  ] as const
                ).map(([key, label, min, max, step]) => (
                  <label key={key} className="block space-y-1">
                    <span className="text-xs text-[var(--home-muted-on-dark)]">{label}</span>
                    <div className="flex gap-2 items-center">
                      <input
                        type="range"
                        min={min}
                        max={max}
                        step={step}
                        value={iv[key as keyof typeof iv]}
                        onChange={(e) => setIv({ ...iv, [key]: Number(e.target.value) })}
                        className="flex-1 accent-blue-500"
                      />
                      <input
                        type="number"
                        value={iv[key as keyof typeof iv]}
                        onChange={(e) => setIv({ ...iv, [key]: Number(e.target.value) })}
                        className={`w-24 py-1 text-sm ${SITE_FORM_CONTROL}`}
                      />
                    </div>
                  </label>
                ))}
              </div>
              <div className="space-y-4">
                <h3 className="font-medium text-[var(--home-text-on-dark)]">Results</h3>
                <p className="text-[var(--home-muted-on-dark)]">
                  Annual rent increase: <span className="font-medium text-[var(--home-text-on-dark)]">{fmt$(ivRes.annualRentIncrease)}</span>
                </p>
                <div className="rounded-xl border border-blue-500/40 bg-blue-950/30 p-4">
                  <p className="text-xs text-[var(--home-muted-on-dark)]">Property value increase</p>
                  <p className="text-2xl font-semibold text-blue-300 mt-1">{fmt$(ivRes.propertyValueIncrease)}</p>
                </div>
                <p className="text-xs text-[var(--home-muted-on-dark)] leading-relaxed">
                  Annual rent increase = Units × Rent increase × Occupancy × 12. Value increase = Annual ÷ Cap rate
                  (0% cap → $0).
                </p>
              </div>
            </div>
          )}
          {tab === 'underwriting' && (
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="font-medium text-[var(--home-text-on-dark)]">Deal inputs</h3>
                {(
                  [
                    ['units', 'No. of units', 1, 500, 1],
                    ['rentPerUnitMo', 'Rent / unit ($/mo)', 300, 6000, 50],
                    ['otherIncomePct', 'Other income (%)', 0, 10, 0.5],
                    ['occupancyPct', 'Occupancy (%)', 1, 100, 1],
                    ['expensesPct', 'Expenses (%)', 20, 70, 1],
                    ['capRatePct', 'Cap rate (%)', 1, 20, 0.25],
                  ] as const
                ).map(([key, label, min, max, step]) => (
                  <label key={key} className="block space-y-1">
                    <span className="text-xs text-[var(--home-muted-on-dark)]">{label}</span>
                    <div className="flex gap-2 items-center">
                      <input
                        type="range"
                        min={min}
                        max={max}
                        step={step}
                        value={uw[key as keyof typeof uw]}
                        onChange={(e) => setUw({ ...uw, [key]: Number(e.target.value) })}
                        className="flex-1 accent-blue-500"
                      />
                      <input
                        type="number"
                        value={uw[key as keyof typeof uw]}
                        onChange={(e) => setUw({ ...uw, [key]: Number(e.target.value) })}
                        className={`w-24 py-1 text-sm ${SITE_FORM_CONTROL}`}
                      />
                    </div>
                  </label>
                ))}
              </div>
              <div className="space-y-3">
                <h3 className="font-medium text-[var(--home-text-on-dark)]">Results</h3>
                <ul className="space-y-2 text-[var(--home-muted-on-dark)]">
                  <li>Annual income: {fmt$(uwRes.annualIncome)}</li>
                  <li>Other income: {fmt$(uwRes.otherIncome)}</li>
                  <li>Gross income: {fmt$(uwRes.grossIncome)}</li>
                  <li>Expenses: {fmt$(uwRes.expenses)}</li>
                  <li>NOI: {fmt$(uwRes.noi)}</li>
                </ul>
                <div className="rounded-xl border border-blue-500/40 bg-blue-950/30 p-4">
                  <p className="text-xs text-[var(--home-muted-on-dark)]">Price to offer</p>
                  <p className="text-2xl font-semibold text-blue-300 mt-1">{fmt$(uwRes.offerPrice)}</p>
                </div>
              </div>
            </div>
          )}
          {tab === 'debt' && (
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <h3 className="font-medium text-[var(--home-text-on-dark)]">Inputs</h3>
                {(
                  [
                    ['purchasePrice', 'Purchase price'],
                    ['grossRentalIncomeYr', 'Gross rental income ($/yr)'],
                    ['otherIncomeYr', 'Other income ($/yr)'],
                    ['operatingExpensesYr', 'Operating expenses ($/yr)'],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key} className="block space-y-1">
                    <span className="text-xs text-[var(--home-muted-on-dark)]">{label}</span>
                    <input
                      type="number"
                      value={debt[key as keyof typeof debt] as number}
                      onChange={(e) => setDebt({ ...debt, [key]: Number(e.target.value) })}
                      className={`w-full ${SITE_FORM_CONTROL}`}
                    />
                  </label>
                ))}
                <label className="block space-y-1">
                  <span className="text-xs text-[var(--home-muted-on-dark)]">Units</span>
                  <input
                    type="number"
                    value={debt.units}
                    onChange={(e) => setDebt({ ...debt, units: Number(e.target.value) })}
                    className={`w-full ${SITE_FORM_CONTROL}`}
                  />
                </label>
                {(
                  [
                    ['vacancyPct', 'Vacancy (%)', 0, 50, 0.5],
                    ['ltvPct', 'LTV (%)', 50, 90, 1],
                    ['interestRatePct', 'Interest rate (%)', 1, 15, 0.125],
                    ['amortYears', 'Amortization (yrs)', 10, 40, 1],
                  ] as const
                ).map(([key, label, min, max, step]) => (
                  <label key={key} className="block space-y-1">
                    <span className="text-xs text-[var(--home-muted-on-dark)]">{label}</span>
                    <div className="flex gap-2 items-center">
                      <input
                        type="range"
                        min={min}
                        max={max}
                        step={step}
                        value={debt[key as keyof typeof debt] as number}
                        onChange={(e) => setDebt({ ...debt, [key]: Number(e.target.value) })}
                        className="flex-1 accent-blue-500"
                      />
                      <input
                        type="number"
                        value={debt[key as keyof typeof debt] as number}
                        onChange={(e) => setDebt({ ...debt, [key]: Number(e.target.value) })}
                        className={`w-20 py-1 text-sm ${SITE_FORM_CONTROL}`}
                      />
                    </div>
                  </label>
                ))}
              </div>
              <div className="space-y-3">
                <h3 className="font-medium text-[var(--home-text-on-dark)]">Results</h3>
                <ul className="space-y-2 text-sm text-[var(--home-muted-on-dark)]">
                  <li>Effective gross income: {fmt$(debtRes.effectiveGrossIncome)}</li>
                  <li>NOI: {fmt$(debtRes.noi)}</li>
                  <li>Loan: {fmt$(debtRes.loanAmount)}</li>
                  <li>Equity: {fmt$(debtRes.equityRequired)}</li>
                  <li>Annual debt service: {fmt$(debtRes.annualDebtService)}</li>
                  <li>Monthly payment: {fmt$(debtRes.monthlyPayment)}</li>
                  <li>Cap rate (NOI/price): {debtRes.stabilizedCapRatePct.toFixed(2)}%</li>
                  <li>Breakeven occ.: {debtRes.breakevenOccupancyPct.toFixed(1)}%</li>
                  <li>$/unit: {fmt$(debtRes.pricePerUnit)}</li>
                </ul>
                <div className="rounded-xl border border-blue-500/40 bg-blue-950/30 p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-[var(--home-muted-on-dark)]">DSCR</p>
                    <p className="text-2xl font-semibold text-blue-300">{debtRes.dscr.toFixed(2)}×</p>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      debtRes.dscr >= 1.2 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'
                    }`}
                  >
                    {debtRes.dscr >= 1.2 ? 'Passes' : 'Review'}
                  </span>
                </div>
              </div>
            </div>
          )}
          {tab === 'equity' && (
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-3">
                <h3 className="font-medium text-[var(--home-text-on-dark)]">Deal economics</h3>
                {(
                  [
                    ['totalProjectCost', 'Total project cost', 0, 100_000_000, 100_000],
                    ['loanAmount', 'Loan amount', 0, 100_000_000, 100_000],
                    ['annualNoi', 'Annual NOI', 0, 10_000_000, 10_000],
                    ['annualLoanPayment', 'Annual loan payment', 0, 5_000_000, 1000],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key} className="block space-y-1">
                    <span className="text-xs text-[var(--home-muted-on-dark)]">{label}</span>
                    <input
                      type="number"
                      value={eq[key as keyof typeof eq] as number}
                      onChange={(e) => setEq({ ...eq, [key]: Number(e.target.value) })}
                      className={`w-full ${SITE_FORM_CONTROL}`}
                    />
                  </label>
                ))}
                {(
                  [
                    ['holdingYears', 'Holding (yrs)', 1, 20, 1],
                    ['exitCapRatePct', 'Exit cap (%)', 3, 12, 0.25],
                    ['gpEquityPct', 'GP equity (%)', 0, 50, 1],
                    ['preferredReturnLpPct', 'Pref to LP (%)', 0, 15, 0.5],
                    ['gpPromotePct', 'GP promote (%)', 0, 50, 1],
                  ] as const
                ).map(([key, label, min, max, step]) => (
                  <label key={key} className="block space-y-1">
                    <span className="text-xs text-[var(--home-muted-on-dark)]">{label}</span>
                    <div className="flex gap-2 items-center">
                      <input
                        type="range"
                        min={min}
                        max={max}
                        step={step}
                        value={eq[key as keyof typeof eq] as number}
                        onChange={(e) => setEq({ ...eq, [key]: Number(e.target.value) })}
                        className="flex-1 accent-blue-500"
                      />
                      <input
                        type="number"
                        value={eq[key as keyof typeof eq] as number}
                        onChange={(e) => setEq({ ...eq, [key]: Number(e.target.value) })}
                        className={`w-20 py-1 text-sm ${SITE_FORM_CONTROL}`}
                      />
                    </div>
                  </label>
                ))}
              </div>
              <div className="space-y-3">
                <h3 className="font-medium text-[var(--home-text-on-dark)]">Capital stack (illustrative)</h3>
                <ul className="space-y-1 text-sm text-[var(--home-muted-on-dark)]">
                  <li>Total equity: {fmt$(eqRes.totalEquity)}</li>
                  <li>GP equity: {fmt$(eqRes.gpEquity)}</li>
                  <li>LP equity: {fmt$(eqRes.lpEquity)}</li>
                  <li>Loan to cost: {eqRes.loanToCostPct.toFixed(1)}%</li>
                  <li>Cash flow to equity (yr): {fmt$(eqRes.annualCashFlowToEquity)}</li>
                  <li>Exit value (NOI/exit cap): {fmt$(eqRes.exitValue)}</li>
                  <li>Exit proceeds (simplified): {fmt$(eqRes.exitProceedsToEquity)}</li>
                </ul>
                <div className="rounded-xl border border-blue-500/40 bg-blue-950/30 p-4">
                  <p className="text-xs text-[var(--home-muted-on-dark)]">Blended equity multiple (model)</p>
                  <p className="text-2xl font-semibold text-blue-300 mt-1">{eqRes.equityMultiple.toFixed(2)}×</p>
                </div>
                <p className="text-xs text-[var(--home-muted-on-dark)]">
                  Illustrative waterfall — refine in Excel for firm IC memos.
                </p>
              </div>
            </div>
          )}
        </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
